import React, { useState, useEffect, useRef } from 'react';
import { Search, Check, ChevronDown, X } from 'lucide-react';

export const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = 'Seleccionar...',
  searchPlaceholder = 'Buscar...',
  renderOption,
  getOptionLabel = (opt) => opt.label,
  getOptionValue = (opt) => opt.value,
  disabled = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filteredOptions = options.filter(opt => 
    getOptionLabel(opt).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedOption = options.find(opt => getOptionValue(opt) === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (opt) => {
    onChange(getOptionValue(opt));
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between px-3 py-2.5 sm:py-2 text-left rounded-xl
          bg-black border border-white/20 text-white text-sm
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-white/40 cursor-pointer'}
          ${isOpen ? 'border-gold' : ''}
        `}
      >
        <span className={selectedOption ? 'text-white' : 'text-white/40'}>
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <div className="flex items-center gap-1">
          {selectedOption && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-white/10 rounded"
            >
              <X className="w-3 h-3 text-white/40" />
            </button>
          )}
          <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-[#0A0A0A] border border-white/20 shadow-xl rounded-xl overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-3 py-2.5 sm:py-2 bg-black/50 border border-white/10 text-white text-sm placeholder-white/40 focus:border-gold outline-none rounded-lg"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-white/40 text-sm">
                No se encontraron resultados
              </div>
            ) : (
              filteredOptions.map((opt, index) => {
                const isSelected = getOptionValue(opt) === value;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelect(opt)}
                    className={`
                      w-full px-3 py-3 sm:py-2 text-left flex items-center justify-between
                      ${isSelected ? 'bg-gold/10 text-gold' : 'text-white hover:bg-white/5 active:bg-white/10'}
                    `}
                  >
                    {renderOption ? renderOption(opt) : (
                      <span className="text-sm">{getOptionLabel(opt)}</span>
                    )}
                    {isSelected && <Check className="w-4 h-4" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
